/**
 * Widget Shell — Shadow DOM container using exact Webflow design
 * Replicates greeeter__chatwidget-* and gr-livetrans__* from igreet-widget.webflow
 */
import { el } from '../utils.js';
import { createMessagesPanel, addMessageToPanel } from './messages.js';
import { createInputArea } from './voice.js';
import normalizeCSS from './igreet-widget.webflow/css/normalize.css?inline';
import componentsCSS from './igreet-widget.webflow/css/components.css?inline';
import webflowCSS from './igreet-widget.webflow/css/igreet-widget.css?inline';
import avatarImgUrl from './igreet-widget.webflow/images/smarties-head-p-130x130q80.png';
import closeIconUrl from './igreet-widget.webflow/images/smarties-x.svg';
import { logger } from '../logger.js';

// ── Asset URLs ──────────────
const CHAT_ICON_URL = 'https://cdn.prod.website-files.com/67ee6a2e2b4993b208083866/67efbf691923fc6908f56d0c_smarties-icon-chat.svg';

export function createShell(widget) {
    // ── Widget Host Container ──────────────────────────────
    const host = el('div', { id: 'igreet-widget-host' });
    const shadow = host;

    // ── Inject all Webflow CSS (normalize + components + widget) + overrides ──
    const styleEl = document.createElement('style');
    styleEl.textContent = normalizeCSS + '\n' + componentsCSS + '\n' + webflowCSS + `
        /* ── Widget boundary reset & styling ── */
        :host, #igreet-widget-host {
            color: #1e293b !important;
            font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
        }

        /* ── Widget positioning overrides ─────────── */
        #igreet-widget-host .page-wrap,
        .page-wrap {
            width: auto; height: auto;
            position: fixed; bottom: 0; right: 0;
            z-index: 2147483647;
            font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            color: #1e293b;
            pointer-events: none;
        }
        #igreet-widget-host .page-wrap * { pointer-events: auto; }
        .greeeter__chatwidget-wrap { position: relative; }

        /* ── Panel open/close (Webflow uses IX2 for this) ── */
        .greeeter__chatwidget-main.is-open {
            display: block;
        }
        .greeeter__chatwidget-btn.is-hidden { display: none; }

        /* ── Embedded Mode (Chat Box) ─────────────── */
        :host(.is-embedded), #igreet-widget-host.is-embedded,
        .is-embedded .page-wrap,
        .is-embedded.page-wrap {
            position: relative !important;
            inset: auto !important;
            bottom: auto !important;
            right: auto !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            height: 100% !important;
            min-height: 480px !important;
            z-index: 1 !important;
            pointer-events: auto !important;
        }

        /* ── Contained Mode (Mounted inside Sandbox / Website Canvas) ── */
        :host(.is-contained), #igreet-widget-host.is-contained,
        :host(.is-contained) .page-wrap,
        .is-contained .page-wrap,
        .is-contained.page-wrap {
            position: absolute !important;
            z-index: 1000 !important;
            bottom: 0 !important;
            right: 0 !important;
            left: auto !important;
            top: auto !important;
            width: auto !important;
            height: auto !important;
            pointer-events: none !important;
        }
        :host(.is-contained) .page-wrap *,
        .is-contained .page-wrap * {
            pointer-events: auto !important;
        }
        :host(.is-contained) .greeeter__chatwidget-wrap,
        .is-contained .greeeter__chatwidget-wrap {
            position: relative !important;
            width: auto !important;
            height: auto !important;
        }
        :host(.is-contained) .greeeter__chatwidget-main,
        .is-contained .greeeter__chatwidget-main {
            position: absolute !important;
            bottom: 74px !important;
            right: 0 !important;
            width: 320px !important;
            max-width: 320px !important;
            margin-right: 16px !important;
            margin-bottom: 0 !important;
            box-shadow: 0 12px 36px rgba(0, 0, 0, 0.18) !important;
            border-radius: 12px !important;
            box-sizing: border-box !important;
        }
        :host(.is-contained) .greeeter__chatwidget-btn,
        .is-contained .greeeter__chatwidget-btn {
            margin: 16px !important;
        }
        .is-embedded .greeeter__chatwidget-wrap {
            width: 100% !important;
            height: 100% !important;
            position: relative !important;
        }
        .is-embedded .greeeter__chatwidget-main {
            position: relative !important;
            display: flex !important;
            flex-direction: column !important;
            width: 100% !important;
            max-width: 100% !important;
            height: 100% !important;
            min-height: 480px !important;
            box-shadow: 0 4px 20px rgba(0,0,0,0.06) !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 12px !important;
            bottom: auto !important;
            right: auto !important;
            left: auto !important;
            top: auto !important;
        }
        .is-embedded .greeeter__chatwidget-btn,
        .is-embedded .greeeter__chatwidget-close {
            display: none !important;
        }

        /* ── Constrain images inside switch/mode buttons ── */
        .gr-livetrans__type-switch img,
        .gr-livetrans__talk-switch img { max-width: 20px; max-height: 20px; }
        .gr-livetrans__mode-icon img { max-width: 80%; max-height: 80%; }

        /* ── Fix switch button visibility (Webflow uses white for dark bg) ── */
        .gr-livetrans__talk-switch,
        .gr-livetrans__type-switch {
            color: rgba(29, 29, 29, 0.67) !important;
            opacity: 0.8;
        }
        .gr-livetrans__talk-switch:hover,
        .gr-livetrans__type-switch:hover { opacity: 1; }

        /* ── Font sizing + text color for chat bubbles ── */
        .greeeter__chatwidget-bubble-inbound,
        .greeeter__chatwidget-bubble-outbound { font-size: 14px; line-height: 1.5; color: #1e293b !important; }
        .greeeter__chatwidget-chat-time { font-size: 12px; color: rgba(0,0,0,0.54); }
        .greeeter__chatwidget-main { color: #1e293b; }
        .gr-chat-input-field { color: #414651 !important; }
        .gr-chat-input-field::placeholder { color: #94a3b8 !important; }

        /* ── State toggle classes ── */
        .gr-livetrans__type-row-input.is-active { display: flex; }
        .gr-livetrans__text-mode.is-active { display: flex; }
        .gr-livetrans__talk-switch.is-active { display: flex; }
        .gr-livetrans__listening.is-active { display: flex; }
        .gr-livetrans__status-processing.is-active { display: flex; }
        .gr-livetrans__status-listening.is-active { display: flex; }

        /* ── Hide elements via state class ── */
        .gr-livetrans__voice-mode.is-hidden,
        .gr-livetrans__text-mode.is-hidden,
        .gr-livetrans__talk-switch.is-hidden,
        .gr-livetrans__type-switch.is-hidden,
        .gr-livetrans__status-idle.is-hidden { display: none !important; }

        /* ── Waveform animation for listening state ── */
        .gr-livetrans__status-listening {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
        }
        .gr-waveform {
            display: flex;
            align-items: center;
            gap: 3px;
            height: 20px;
        }
        .gr-waveform__bar {
            width: 3px;
            height: 4px;
            background: rgba(180, 60, 60, 0.7);
            border-radius: 2px;
            animation: gr-wave 0.8s ease-in-out infinite alternate;
        }
        @keyframes gr-wave {
            0% { height: 4px; }
            50% { height: 16px; }
            100% { height: 4px; }
        }

        /* ── Loading overlay for LiveKit connection ── */
        .gr-loading-overlay {
            display: none;
            position: absolute;
            inset: 0;
            background: rgba(255, 255, 255, 0.85);
            z-index: 100;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 12px;
            border-radius: 10px;
        }
        .gr-loading-overlay.is-active { display: flex; }
        .gr-loading-spinner {
            width: 32px; height: 32px;
            border: 3px solid #e0e0e0;
            border-top-color: #004E61;
            border-radius: 50%;
            animation: gr-spin 0.8s linear infinite;
        }
        .gr-loading-text {
            font-size: 13px; color: #555;
            font-weight: 500;
        }
        @keyframes gr-spin {
            to { transform: rotate(360deg); }
        }

        /* ── Typing indicator ── */
        .gr-typing-indicator {
            display: none;
            align-items: center;
            gap: 8px;
            padding: 6px 16px;
            font-size: 13px;
            color: #888;
        }
        .gr-typing-indicator.is-active { display: flex; }
        .gr-typing-dots {
            display: flex; gap: 3px; align-items: center;
        }
        .gr-typing-dots span {
            width: 5px; height: 5px; border-radius: 50%;
            background: #aaa;
            animation: gr-bounce 1.2s ease-in-out infinite;
        }
        .gr-typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .gr-typing-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes gr-bounce {
            0%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-6px); }
        }

        /* ── Text-to-Speech (TTS) Listen Button ── */
        .chat-tts-btn {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 4px !important;
            background: rgba(0, 78, 97, 0.08) !important;
            color: #004E61 !important;
            border: 1px solid rgba(0, 78, 97, 0.2) !important;
            border-radius: 9999px !important;
            padding: 2px 8px !important;
            font-size: 11px !important;
            font-weight: 600 !important;
            font-family: inherit !important;
            cursor: pointer !important;
            transition: all 0.15s ease !important;
            outline: none !important;
            margin: 0 0 0 6px !important;
            line-height: 1.2 !important;
            white-space: nowrap !important;
            box-sizing: border-box !important;
            height: 20px !important;
            min-height: 20px !important;
            max-height: 20px !important;
            width: auto !important;
            min-width: auto !important;
            -webkit-appearance: none !important;
            appearance: none !important;
            flex-shrink: 0 !important;
        }
        .chat-tts-btn:hover {
            background: rgba(0, 78, 97, 0.15) !important;
            border-color: rgba(0, 78, 97, 0.35) !important;
        }
        .chat-tts-btn.is-playing {
            background: #004E61 !important;
            color: #ffffff !important;
            border-color: #004E61 !important;
        }

        /* ── Image reference badges & cards ── */
        .image-reference-badge {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            background: rgba(0, 78, 97, 0.08);
            color: #004E61;
            border-radius: 4px;
            padding: 1px 6px;
            font-size: 0.75rem;
            font-weight: 600;
            font-family: inherit;
            margin: 0 2px;
            vertical-align: middle;
            line-height: 1.4;
        }

        .chat-referenced-images {
            margin-top: 8px;
            margin-bottom: 2px;
            display: flex;
            flex-direction: column;
            gap: 6px;
            width: 100%;
            max-width: 250px;
        }

        .chat-referenced-image-card {
            border-radius: 10px;
            overflow: hidden;
            border: 1px solid #e2e8f0;
            background: #ffffff;
            padding: 6px;
            box-sizing: border-box;
            width: 100%;
            box-shadow: 0 2px 6px rgba(0,0,0,0.06);
            cursor: pointer;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .chat-referenced-image-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(0,0,0,0.12);
        }

        .chat-referenced-image-img {
            width: 100%;
            height: 140px;
            object-fit: cover;
            border-radius: 6px;
            display: block;
            background: #f1f5f9;
        }

        .chat-referenced-image-title {
            font-size: 12px;
            font-weight: 600;
            color: #0f172a;
            margin-top: 6px;
            padding: 0 2px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .chat-referenced-image-desc {
            font-size: 11px;
            color: #64748b;
            padding: 0 2px;
            margin-top: 2px;
            line-height: 1.35;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        /* ── Widget Image Lightbox ── */
        .gr-lightbox-overlay {
            display: none;
            position: fixed;
            inset: 0;
            background: rgba(15, 23, 42, 0.85);
            z-index: 2147483647;
            align-items: center;
            justify-content: center;
            padding: 20px;
            box-sizing: border-box;
            backdrop-filter: blur(4px);
            animation: grFadeIn 0.2s ease-out;
        }
        .gr-lightbox-overlay.is-active {
            display: flex;
        }
        .gr-lightbox-content {
            position: relative;
            max-width: 90vw;
            max-height: 85vh;
            background: #ffffff;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            display: flex;
            flex-direction: column;
        }
        .gr-lightbox-close {
            position: absolute;
            top: 10px;
            right: 10px;
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.6);
            color: #ffffff;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 16px;
            font-weight: bold;
            z-index: 10;
            transition: background 0.15s;
        }
        .gr-lightbox-close:hover {
            background: rgba(0, 0, 0, 0.8);
        }
        .gr-lightbox-img {
            max-width: 85vw;
            max-height: 70vh;
            object-fit: contain;
            display: block;
            background: #0f172a;
        }
        .gr-lightbox-caption {
            padding: 12px 16px;
            background: #ffffff;
            border-top: 1px solid #e2e8f0;
        }
        .gr-lightbox-title {
            font-size: 14px;
            font-weight: 600;
            color: #1e293b;
        }
        .gr-lightbox-desc {
            font-size: 12px;
            color: #64748b;
            margin-top: 4px;
            line-height: 1.4;
        }
        @keyframes grFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
        }
    `;
    shadow.appendChild(styleEl);

    // Load Inter font in the main document if not already loaded
    if (!document.querySelector('link[href*="fonts.googleapis.com/css2?family=Inter"]')) {
        const fontLink = document.createElement('link');
        fontLink.rel = 'stylesheet';
        fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap';
        document.head.appendChild(fontLink);
    }

    // ── Build DOM matching Webflow structure ──────────
    const pageWrap = el('div', { className: 'page-wrap' });
    const wrap = el('div', { className: 'greeeter__chatwidget-wrap' });
    pageWrap.appendChild(wrap);

    // ─ Floating bubble button ────────────────────────
    const bubbleImg = el('img', { src: avatarImgUrl, loading: 'lazy', alt: '' });
    // Start hidden — will be revealed once config decides which logo to show.
    // This prevents the default avatar from flashing before a custom logo loads.
    bubbleImg.style.opacity = '0';
    bubbleImg.style.transition = 'opacity 0.2s ease';
    const defaultAvatarSrc = avatarImgUrl; // Keep reference to default
    const chatMenuAvatar = el('div', { className: 'greeeter__chatmenu-avatar' }, [
        el('img', { src: CHAT_ICON_URL, loading: 'lazy', alt: '', className: 'image-100' }),
    ]);
    const bubble = el('div', { className: 'greeeter__chatwidget-btn', onClick: () => widget.open() }, [
        bubbleImg, chatMenuAvatar,
    ]);
    wrap.appendChild(bubble);

    // ─ Chat panel ────────────────────────────────────
    const panel = el('div', { className: 'greeeter__chatwidget-main' });

    // Header
    const headerName = el('div', { className: 'greeeter__chatwidget-main-name' }, 'Customers.new');
    const closeIconWrap = el('div', { className: 'greeeter__chatwidget-close', onClick: () => widget.close() });
    closeIconWrap.innerHTML = `<img loading="lazy" src="${closeIconUrl}" alt="">`;
    const header = el('div', { className: 'greeeter__chatwidget-main-top' }, [headerName, closeIconWrap]);
    panel.appendChild(header);

    // Messages area
    const messagesPanel = createMessagesPanel();
    panel.appendChild(messagesPanel);

    // Typing indicator (shown when agent is typing)
    const typingIndicator = el('div', { className: 'gr-typing-indicator' }, [
        el('div', { className: 'gr-typing-dots' }, [
            el('span'), el('span'), el('span'),
        ]),
        el('div', {}, 'Agent is typing...'),
    ]);
    panel.appendChild(typingIndicator);
    let typingHideTimer = null;

    // Loading overlay (shown during LiveKit connection)
    const loadingOverlay = el('div', { className: 'gr-loading-overlay' }, [
        el('div', { className: 'gr-loading-spinner' }),
        el('div', { className: 'gr-loading-text' }, 'Connecting...'),
    ]);
    panel.appendChild(loadingOverlay);
    // Make panel position:relative for the overlay
    panel.style.position = 'relative';

    // Input area (voice + text controls)
    const { inputAreaEl, inputField, voiceMode, textMode, talkSwitch, typeSwitch,
        talkDiv, statusDiv, statusProcessing, statusIdle, statusListening,
        micNewBtn, micListeningBtn, typeRowInput } = createInputArea(widget);
    panel.appendChild(inputAreaEl);

    wrap.appendChild(panel);
    shadow.appendChild(pageWrap);

    // Track current mode
    let currentMode = 'text';

    // ── setMode as a local function (used by click handlers + shell API)
    function setModeLocal(mode) {
        currentMode = mode;
        if (mode === 'voice') {
            // Show: voice indicator, mic button, status, "switch to typing"
            voiceMode.style.display = 'flex';
            talkDiv.style.display = 'flex';
            statusDiv.style.display = 'block';
            typeSwitch.style.display = 'flex';
            // Hide: text indicator, text input, "switch to voice"
            textMode.style.display = 'none';
            typeRowInput.style.display = 'none';
            talkSwitch.style.display = 'none';
            // Reset listening to idle
            micNewBtn.style.display = 'flex';
            micListeningBtn.style.display = 'none';
            statusIdle.style.display = 'flex';
            statusListening.style.display = 'none';
        } else {
            // Show: text indicator, text input, "switch to voice"
            textMode.style.display = 'flex';
            typeRowInput.style.display = 'flex';
            talkSwitch.style.display = 'flex';
            // Hide: voice indicator, mic button, status, "switch to typing"
            voiceMode.style.display = 'none';
            talkDiv.style.display = 'none';
            statusDiv.style.display = 'none';
            typeSwitch.style.display = 'none';
            inputField.focus();
        }
    }

    // Wire up mode switch click handlers → calls widget.setMode() for LiveKit
    typeSwitch.style.cursor = 'pointer';
    typeSwitch.addEventListener('click', () => {
        setModeLocal('text');
        widget.setMode?.('text');
    });
    talkSwitch.style.cursor = 'pointer';
    talkSwitch.addEventListener('click', () => {
        setModeLocal('voice');
        widget.setMode?.('voice');
    });

    // ── Mic button listening toggle → calls widget.toggleMic() for LiveKit ──
    let isListening = false;
    function setListeningState(listening) {
        isListening = listening;
        if (listening) {
            micNewBtn.style.display = 'none';
            micListeningBtn.style.display = 'flex';
            statusIdle.style.display = 'none';
            statusListening.style.display = 'flex';
        } else {
            micNewBtn.style.display = 'flex';
            micListeningBtn.style.display = 'none';
            statusIdle.style.display = 'flex';
            statusListening.style.display = 'none';
        }
    }

    talkDiv.style.cursor = 'pointer';
    talkDiv.addEventListener('click', async () => {
        const newState = !isListening;
        setListeningState(newState);
        // Toggle actual microphone via LiveKit
        try {
            await widget.toggleMic?.();
        } catch (err) {
            logger.error('[iGreet] Mic toggle failed:', err);
        }
    });

    // Set initial mode to text (before panel is shown)
    setModeLocal('text');

    // ── Config helper — applies position + theme immediately ────
    // Dynamic style element for overriding Webflow hardcoded values
    let dynamicStyleEl = null;
    let currentConfig = null;

    function applyConfig(config) {
        currentConfig = config;
        const t = config?.theme;
        if (!t) return;

        // Position
        const pos = t.position;
        if (pos === 'bottom-left') {
            pageWrap.style.right = 'auto';
            pageWrap.style.left = '0';
            wrap.style.inset = 'auto auto 0% 0%';
            bubble.style.right = 'auto';
            bubble.style.left = '0';
            bubble.style.marginRight = '0';
            bubble.style.marginLeft = '1.5vw';
            panel.style.inset = 'auto auto 71px 0%';
            panel.style.marginRight = '0';
            panel.style.marginLeft = '1vw';
        } else {
            pageWrap.style.left = 'auto';
            pageWrap.style.right = '0';
            wrap.style.inset = '';
            bubble.style.right = '';
            bubble.style.left = '';
            bubble.style.marginRight = '';
            bubble.style.marginLeft = '';
            panel.style.inset = '';
            panel.style.marginRight = '';
            panel.style.marginLeft = '';
        }

        // Theme colors (applied early so bubble color is correct before panel opens)
        if (t.primaryColor) {
            bubble.style.backgroundColor = t.primaryColor;
            header.style.backgroundColor = t.primaryColor;
        }
        if (t.textColor) {
            header.style.color = t.textColor;
            panel.style.color = t.textColor;
        }
        if (t.widgetTitle) {
            headerName.textContent = t.widgetTitle;
        }

        // Typography — font family
        if (t.fontFamily) {
            pageWrap.style.fontFamily = `${t.fontFamily}, sans-serif`;
            const fontName = t.fontFamily.replace(/\s+/g, '+');
            const fontHref = `https://fonts.googleapis.com/css2?family=${fontName}:wght@300;400;500;600;700&display=swap`;
            if (!document.querySelector(`link[href*="family=${fontName}"]`)) {
                const link = document.createElement('link');
                link.rel = 'stylesheet';
                link.href = fontHref;
                document.head.appendChild(link);
            }
        }

        // Typography + text color — inject into shadow DOM to override Webflow's hardcoded values
        if (t.fontSize || t.textColor) {
            if (!dynamicStyleEl) {
                dynamicStyleEl = document.createElement('style');
                shadow.appendChild(dynamicStyleEl);
            }
            const fs = t.fontSize || '14px';
            const tc = t.textColor || '';
            const colorRule = tc ? `color: ${tc} !important;` : '';
            dynamicStyleEl.textContent = `
                .greeeter__chatwidget-mainconvo,
                .greeeter__chatwidget-bubble-outbound,
                .greeeter__chatwidget-bubble-inbound,
                .gr-chat-input-field {
                    font-size: ${fs} !important;
                    ${colorRule}
                }
                .greeeter__chatwidget-main {
                    ${colorRule}
                }
                .greeeter__chatwidget-chat-time {
                    ${tc ? `color: ${tc} !important; opacity: 0.6;` : ''}
                }
            `;
        }

        // Custom logo for bubble — preload before revealing to avoid
        // flashing the default avatar while the custom one downloads.
        if (t.logoUrl) {
            // Hide until custom logo is ready
            bubbleImg.style.opacity = '0';
            const preload = new Image();
            preload.onload = () => {
                bubbleImg.src = t.logoUrl;
                bubbleImg.style.objectFit = 'cover';
                bubbleImg.style.borderRadius = '50%';
                bubbleImg.style.opacity = '1';
            };
            preload.onerror = () => {
                // Fallback to default if custom logo fails to load
                bubbleImg.src = defaultAvatarSrc;
                bubbleImg.style.objectFit = '';
                bubbleImg.style.borderRadius = '';
                bubbleImg.style.opacity = '1';
            };
            preload.src = t.logoUrl;
            chatMenuAvatar.style.display = 'none';
            currentLogoUrl = t.logoUrl;
        } else {
            bubbleImg.src = defaultAvatarSrc;
            bubbleImg.style.objectFit = '';
            bubbleImg.style.borderRadius = '';
            bubbleImg.style.opacity = '1';
            chatMenuAvatar.style.display = '';
            currentLogoUrl = null;
        }

        // Bubble size (default 60px, range 40–120)
        if (t.logoSize) {
            const size = Math.max(40, Math.min(120, Number(t.logoSize) || 60));
            bubble.style.width = size + 'px';
            bubble.style.height = size + 'px';
            bubbleImg.style.width = '100%';
            bubbleImg.style.height = '100%';
        }
    }

    // Track custom logo URL for message avatars
    let currentLogoUrl = null;

    // ── Full-screen Image Lightbox in Shadow DOM ─────
    const lightboxOverlay = el('div', { className: 'gr-lightbox-overlay' });
    const lightboxContent = el('div', { className: 'gr-lightbox-content' });
    const lightboxCloseBtn = el('button', { className: 'gr-lightbox-close', ariaLabel: 'Close' }, '✕');
    const lightboxImg = el('img', { className: 'gr-lightbox-img', src: '', alt: '' });
    const lightboxCaption = el('div', { className: 'gr-lightbox-caption' });
    const lightboxTitle = el('div', { className: 'gr-lightbox-title' });
    const lightboxDesc = el('div', { className: 'gr-lightbox-desc' });

    lightboxCaption.appendChild(lightboxTitle);
    lightboxCaption.appendChild(lightboxDesc);
    lightboxContent.appendChild(lightboxCloseBtn);
    lightboxContent.appendChild(lightboxImg);
    lightboxContent.appendChild(lightboxCaption);
    lightboxOverlay.appendChild(lightboxContent);
    shadow.appendChild(lightboxOverlay);

    const closeLightbox = () => {
        lightboxOverlay.classList.remove('is-active');
        lightboxImg.src = '';
    };
    lightboxCloseBtn.addEventListener('click', (e) => { e.stopPropagation(); closeLightbox(); });
    lightboxOverlay.addEventListener('click', (e) => {
        if (e.target === lightboxOverlay) closeLightbox();
    });

    // ── Shell API ────────────────────────────────────
    const shellApi = {
        container: host,
        widget,
        get config() { return currentConfig || widget?.config; },

        openLightbox({ url, title, description }) {
            lightboxImg.src = url || '';
            lightboxImg.alt = title || '';
            lightboxTitle.textContent = title || '';
            lightboxDesc.textContent = description || '';
            lightboxDesc.style.display = description ? 'block' : 'none';
            lightboxOverlay.classList.add('is-active');
        },

        closeLightbox,

        applyPosition(config) {
            applyConfig(config);
        },

        showPanel(config) {
            // Apply position + colors + title + fonts (unified helper)
            applyConfig(config);

            // Reset mic listening state on every panel open (prevents stuck grey mic)
            setListeningState(false);

            // Default to text mode
            this.setMode('text');

            panel.classList.add('is-open');
            bubble.classList.add('is-hidden');
            inputField.focus();
        },

        hidePanel() {
            // Reset mic listening state so it's clean when widget reopens
            setListeningState(false);
            panel.classList.remove('is-open');
            bubble.classList.remove('is-hidden');
        },

        addMessage(msg) {
            addMessageToPanel(messagesPanel, msg, currentLogoUrl, shellApi);
            // Reliable auto-scroll: scroll to bottom after DOM update
            requestAnimationFrame(() => {
                messagesPanel.scrollTop = messagesPanel.scrollHeight;
            });
        },

        setMode(mode) {
            setModeLocal(mode);
        },

        onStateChange(newState, prevState) {
            wrap.setAttribute('data-state', newState);
            if (newState === 'connecting') {
                statusProcessing.classList.add('is-active');
                statusIdle.classList.add('is-hidden');
            } else {
                statusProcessing.classList.remove('is-active');
                statusIdle.classList.remove('is-hidden');
            }
        },

        onLiveKitState(connectionState) {
            if (connectionState === 'connecting') {
                loadingOverlay.classList.add('is-active');
            } else {
                loadingOverlay.classList.remove('is-active');
            }

            // Reset mic button if LiveKit disconnects (prevents stuck grey mic after idle timeout)
            if (connectionState === 'disconnected') {
                setListeningState(false);
            }
        },

        showTyping() {
            typingIndicator.classList.add('is-active');
            messagesPanel.scrollTop = messagesPanel.scrollHeight;
            if (typingHideTimer) clearTimeout(typingHideTimer);
            typingHideTimer = setTimeout(() => {
                typingIndicator.classList.remove('is-active');
            }, 15000); // 15s safety timeout
        },

        hideTyping() {
            typingIndicator.classList.remove('is-active');
            if (typingHideTimer) { clearTimeout(typingHideTimer); typingHideTimer = null; }
        },
    };

    return shellApi;
}
